import React, { FC, useEffect, useRef, useState } from "react";
import { Meter } from "../../../interfaces/Meter";
import DataTable from 'react-data-table-component';
import { CSVLink } from "react-csv";
import { IonButton, IonDatetime, IonDatetimeButton, IonIcon, IonModal, IonPopover, IonRow, IonSelect, IonSelectOption } from "@ionic/react";
import { settingsOutline, } from "ionicons/icons";
import { formatLocalString, formatUTCString } from "../../../utils/handleDatetime";
import usePermissionGroups from "../../../utils/usePermissionGroups";
import NotificationPopUp from "../../extras/NotificationPopUp";
import { API_URL } from "../../../actions/settings";
import ConfirmDeleteBtnModal from "../Modals/ConfirmDeleteBtnModal";
import useToggle from "../../../utils/useToggle";
import CSVDownloadButtonModal from "../../extras/CSVDownloadButtonModal";


interface MeterDataRow {
  uuid: string;
  meter: Meter;
  value: number;
  units: string;
  hour_diff: number;
  cst_datetime: string;
  timestamp: string;
}

interface IProps {
    id:string;
    data: any;
    name: string | null
    units: string [] | null | undefined;
    url: URL;
    handleFilters:any;
    setUrl:any;
};

async function deleteItem(token:any,data:any) {

    return fetch(`${API_URL}/internal_meter_data/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization':token
      },
      body: JSON.stringify(data)

    })
      .then(data => data.json())
   }


const InternalMeterDataTable:FC<IProps> = (props) => {
    const [opened,setOpened] = useState(false)
    const startDatetimeRef = useRef<HTMLIonDatetimeElement>(null)
    const endDatetimeRef = useRef<HTMLIonDatetimeElement>(null)
    const { hasPermission } = usePermissionGroups()
    const [tableData, setTableData] = useState(props.data.results.ebo_data)
    const [count, setCount] = useState(props.data.count)
    const [selected, setSelected] = useState([])
    const [clearRows, toggleClearRows] = useToggle(false)
    

    useEffect(()=>{
        setTableData(props.data.results.ebo_data)
        setCount(props.data.count)
    },[props.data.results.ebo_data,props.data.count])

    const handleDelete = async (ids:any) =>{
        const token = localStorage.getItem('token') || ""
        if (ids === 'all'){
            const data = {
                'meter_id': props.id
            }
            const res = await deleteItem(`Token ${token.slice(1,-1)}`,data)
            if (res.status === "success"){
                NotificationPopUp('success','All Interval Data Deleted')
                setTableData(
                    []
                )
                setCount(0)
                setSelected([])
                toggleClearRows()

            }
            else{
                NotificationPopUp('error',res.detail,res.error||"Could Not Delete")
            }
        }
        else if (ids && ids !== ''){
            
            const data = {
                'ids': ids
            }
            const res = await deleteItem(`Token ${token.slice(1,-1)}`,data)
            if (res.status === "success"){
                NotificationPopUp('success','Interval Data Deleted')
                setTableData(
                    (prevState:any) => prevState.filter((bill:any) => !ids.includes(bill.uuid))
                )
                setCount((prev:number)=>prev-selected.length)
                setSelected([])
                toggleClearRows()

            }
            else{
                NotificationPopUp('error',res.detail,res.error||"Could Not Delete")
            }
        }
    }


    const units_column = <div >        
        <IonSelect label="Units: " interface="popover" placeholder="All" onIonChange={(e)=>filterData(e.detail.value)}>
                    <IonSelectOption value='all'>All</IonSelectOption>
                    {props.units?.map((unit)=><IonSelectOption key={unit}>{unit}</IonSelectOption>)}
                </IonSelect>
    </div>

    const timestamp_column = <div className="vertical-center">
        <IonButton fill="clear" size="small" onClick={(e)=>{e.stopPropagation();setOpened(true);}}><IonIcon src={settingsOutline} color="light"></IonIcon></IonButton> Timestamp
    </div>    

    const columns = [
    {
        name:timestamp_column,
        selector: (row: MeterDataRow) => new Date(row.timestamp).toLocaleString(),
        sortable: true,
        sortFunction: (a:any, b:any) => {
            // Parse local date-time string into a time value (milliseconds)
            const dateA = new Date(a.timestamp).getTime();
            const dateB = new Date(b.timestamp).getTime();            
            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;
            return 0;
        },
    },
    {
        name:"Value",
        selector: (row: MeterDataRow) => row.value,
        sortable: false,
    },
    {
        name:"Hourly Difference",
        selector: (row: MeterDataRow) => row.hour_diff,
        sortable: false,
    },
    {
        name:units_column,
        selector: (row: MeterDataRow) => row.units,
        sortable: false,
    }
    ]

    const filterData = (units:string) => {
        if (!props.data) return;
        if (props.url.searchParams.has('units')){
            props.url.searchParams.set('units',units)
        }else{
            props.url.searchParams.append('units',units)
        }
        props.handleFilters(props.url.toString())
    }

    const handlePageChange = (page:number) => {
        props.url.searchParams.set('page',(page.toString()))
        props.handleFilters(props.url.toString())
    }

    const handlePerRowsChange = (size:number) => {
        props.url.searchParams.set('page_size',(size.toString()))
        props.handleFilters(props.url.toString())
    }

    const handleTimestampFilterChange = () => {
        if(startDatetimeRef){
            if (props.url.searchParams.has('start_date')){
                props.url.searchParams.set('start_date',formatUTCString(startDatetimeRef.current?.value?.toString()))
            }else{
                props.url.searchParams.append('start_date',formatUTCString(startDatetimeRef.current?.value?.toString()))
            }
        }
        if(endDatetimeRef){
            if (props.url.searchParams.has('end_date')){
                props.url.searchParams.set('end_date',formatUTCString(endDatetimeRef.current?.value?.toString()))
            }else{
                props.url.searchParams.append('end_date',formatUTCString(endDatetimeRef.current?.value?.toString()))
            }
        }
        
        props.handleFilters(props.url.toString())
    }

    const handleResetTimestampFilter = () => {
        if (props.url.searchParams.has('start_date')){
            props.url.searchParams.delete('start_date')
        }
        if (props.url.searchParams.has('end_date')){
            props.url.searchParams.delete('end_date')
        }

        props.handleFilters(props.url.toString())
    }

    const headers = [
        { label: "Timestamp", key: "cst_datetime" },
        { label: "Value", key: "value" },
        { label: "Hourly Difference", key: "hour_diff" },        
        { label: "Units", key: "units" },
      ];

    
    const title = <div>
        <h2>Data</h2>
        <IonRow className="ion-justify-content-end">
            <CSVDownloadButtonModal data ={props.data.results.ebo_data || []} headers={headers} separator=',' filename={`${props.name}_interval_data.csv`} full_download_url={`internal_meter_data/${props.id}/export_csv`}/>
        </IonRow>            
            <IonModal
            className="modal-background center-all"
            style={{'--height':'200px'}}
            id="download-csv-modal"
            isOpen={opened}
            onDidDismiss={() => setOpened(false)}>
                <div style={{
                    margin:'1rem',
                    height:'100%',
                    display:'flex',
                    flexDirection:'column',
                    justifyContent:'center',
                    alignItems:'center',
                    'verticalAlign':'center'
                }}>
                <div style={{width:'100%'}}>
                    <div>
                        <div style={{display:'flex', justifyContent:'space-between'}}>
                            <h3> Start Date and Time: </h3>
                        <IonDatetimeButton id='start_time' datetime="start_datetime">time</IonDatetimeButton>
                        </div>
                        <IonPopover trigger="start_time" triggerAction="click" keepContentsMounted={true}>
                            <IonDatetime
                                id="start_datetime"
                                ref={startDatetimeRef}
                                presentation="date-time"
                                value={formatLocalString(props.data.results.start_datetime)}
                                formatOptions={{
                                    date: {                                    
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: '2-digit',
                                    },
                                    time: {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    },
                                }}
                                ></IonDatetime>
                        </IonPopover>
                    </div>
                    <div>
                        <div style={{display:'flex', justifyContent:'space-between'}}>
                            <h3> End Date and Time: </h3>
                        <IonDatetimeButton id='end_time' datetime="end_datetime">time</IonDatetimeButton>
                        </div>
                        <IonPopover trigger="end_time" triggerAction="click" keepContentsMounted={true}>
                            <IonDatetime
                                id="end_datetime"
                                ref={endDatetimeRef}
                                presentation="date-time"
                                value={formatLocalString(props.data.results.end_datetime)}
                                formatOptions={{
                                    date: {                                    
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: '2-digit',
                                    },
                                    time: {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    },
                                }}
                                ></IonDatetime>
                        </IonPopover>
                    </div>
                    <div style={{display:'flex', justifyContent:'space-between'}}>
                        <IonButton onClick={() => handleResetTimestampFilter()} color='danger'>Reset</IonButton>
                        <IonButton onClick={() => handleTimestampFilterChange()}>Filter</IonButton>
                    </div>
                </div>
                </div>
            </IonModal>
        
    </div>


    const context = <IonRow className="ion-justify-content-between" hidden={selected.length === 0} style={{fontSize:'26px',width:'100%'}}>
            <div style={{display:'flex',alignItems:'center'}} > {selected.length} Selected</div>
            <IonButton  fill='clear'>
                <ConfirmDeleteBtnModal id={'all'} handleDelete={handleDelete} icon={false} buttonText="Delete All">
                    This will delete all data and cannot be undone. Are you Sure?
                </ConfirmDeleteBtnModal>

                <ConfirmDeleteBtnModal id={selected} handleDelete={handleDelete}/>
                
            </IonButton>
        </IonRow>

    const handleSelect = (e:any) => {
        setSelected(e.selectedRows.map((row:MeterDataRow) => row.uuid))
    }

    const customStyles = {
    contextMenu: {
        style: {
        backgroundColor: '#1e1e1e', // your color
        color: '#fff',
        },
    },
    };

    return <DataTable
              title={title}
              columns={columns}
              data={tableData || []}              
              pagination
              paginationServer
              paginationTotalRows={count}
              paginationPerPage={100}
              paginationRowsPerPageOptions={[100, 200, 500, 1000]}
              onChangeRowsPerPage={handlePerRowsChange} 
              onChangePage={handlePageChange}
              theme='dark'
              selectableRows={hasPermission(['canDeleteIntervalData'])}
              onSelectedRowsChange={handleSelect}            
              contextComponent={context}
              customStyles={customStyles}
              clearSelectedRows={clearRows}
    
            />    
};

export default InternalMeterDataTable;